import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import { PdfViewerModal } from "../../../components/crm/contracts/PdfViewerModal";
import { ResponsiveTable } from "../../../components/common/ResponsiveTable";
import type {
  RecoveryAction,
  RecoveryBoard,
  RecoveryDetail,
  RecoveryDraft,
  RecoveryItem,
  RecoveryReceipt,
  RecoveryRound,
  ScopeLine,
} from "../../../types/insuranceRecovery";

const money = (value: string | number) =>
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
const localDate = (value: string | null) =>
  value ? new Date(`${value}T12:00:00`).toLocaleDateString() : "Not scheduled";
const message = (error: unknown) => {
  const data = (error as { responseData?: unknown })?.responseData;
  return data
    ? typeof data === "string"
      ? data
      : JSON.stringify(data)
    : "Unable to save. Please retry.";
};
const labels: Record<RecoveryAction, string> = {
  round: "Draft submission round",
  followup: "Save next action",
  packet: "Assemble PDF packet",
  submit: "Record submission",
  decision: "Record carrier decision",
  receipt: "Record received funds",
  reverse: "Reverse receipt",
  discard: "Discard draft",
};
const field = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.5,
};
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <FormLabel>{label}</FormLabel>
      {children}
    </Box>
  );
}

function ScopeEditor({
  label,
  rows,
  onChange,
  measurement,
}: {
  label: string;
  rows: ScopeLine[];
  onChange: (rows: ScopeLine[]) => void;
  measurement: RecoveryDetail["sources"]["measurement"];
}) {
  const update = (index: number, name: keyof ScopeLine, value: string) =>
    onChange(
      rows.map((row, i) => (i === index ? { ...row, [name]: value } : row)),
    );
  return (
    <Stack spacing={1.5}>
      <Typography level="h4">{label} scope</Typography>
      <Typography level="body-sm">
        Use the same comparison key, category and unit for equivalent work on
        both sides. Cite the page, measurement or documented evidence for each
        row.
      </Typography>
      {rows.map((row, index) => (
        <Card key={index} variant="outlined">
          <Box sx={field}>
            {(
              [
                ["key", "Comparison key"],
                ["description", "Work description"],
                ["category", "Category"],
                ["unit", "Unit"],
                ["quantity", "Quantity"],
                ["unit_price", "Unit price"],
              ] as const
            ).map(([name, title]) => (
              <Field key={name} label={title}>
                <Input
                  aria-label={`${label} ${index + 1} ${title}`}
                  value={row[name]}
                  type={
                    ["quantity", "unit_price"].includes(name)
                      ? "number"
                      : "text"
                  }
                  onChange={(e) => update(index, name, e.target.value)}
                />
              </Field>
            ))}
          </Box>
          <Textarea
            aria-label={`${label} ${index + 1} evidence`}
            placeholder="Source page / evidence"
            value={row.source_note}
            onChange={(e) => update(index, "source_note", e.target.value)}
          />
          <Button
            variant="plain"
            color="danger"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            Remove {label.toLowerCase()} row {index + 1}
          </Button>
        </Card>
      ))}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          onClick={() =>
            onChange([
              ...rows,
              {
                key: "",
                description: "",
                category: "roofing",
                unit: "SQ",
                quantity: "1",
                unit_price: "0",
                source_note: "",
              },
            ])
          }
        >
          Add {label.toLowerCase()} row
        </Button>
        {label === "Contractor" && measurement && (
          <Button
            variant="outlined"
            onClick={() =>
              onChange([
                ...rows,
                {
                  key: "roof-area",
                  description: "Measured roof area",
                  category: "roofing",
                  unit: "SQ",
                  quantity: measurement.squares,
                  unit_price: "0",
                  source_note: `ApexOS ${measurement.kind} ${measurement.id}; ${measurement.area_sqft} square feet. Verify scope and waste separately.`,
                },
              ])
            }
          >
            Use ApexOS roof size
          </Button>
        )}
      </Box>
    </Stack>
  );
}

function DraftForm({
  detail,
  item,
  onSave,
  pending,
  error,
  onCancel,
}: {
  detail: RecoveryDetail;
  item?: RecoveryItem;
  onSave: (data: RecoveryDraft) => void;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
}) {
  const latest = item?.rounds.at(-1);
  const [draft, setDraft] = useState<RecoveryDraft>({
    item: item?.id,
    kind: item?.kind || "supplement",
    title: item?.title || "",
    reference: item?.reference || "",
    requested: latest?.requested || "",
    source_note: "",
    supplement: item?.supplement || null,
    coc: item?.coc || null,
    analysis: latest?.analysis || null,
    source_page: latest?.source_page || null,
    carrier_scope: latest?.carrier_scope || [],
    contractor_scope: latest?.contractor_scope || [],
  });
  const change = <K extends keyof RecoveryDraft>(
    name: K,
    value: RecoveryDraft[K],
  ) => setDraft({ ...draft, [name]: value });
  return (
    <Stack spacing={2} data-testid="recovery-draft-form">
      <Typography level="h3">
        {item ? "New round for " + item.title : "New recovery item"}
      </Typography>
      <Alert>
        Requested and approved amounts are cumulative for one recovery item.
        Resubmitting an existing item replaces its requested total; it does not
        add the same claim twice.
      </Alert>
      {!item && (
        <>
          <Box sx={field}>
            <Field label="Recovery type">
              <Select
                aria-label="Recovery type"
                value={draft.kind}
                onChange={(_, value) =>
                  value &&
                  setDraft({
                    ...draft,
                    kind: value,
                    supplement: null,
                    coc: null,
                  })
                }
              >
                <Option value="supplement">Supplement</Option>
                <Option value="depreciation">Depreciation</Option>
              </Select>
            </Field>
            <Field label="Stable recovery reference">
              <Input
                aria-label="Recovery reference"
                placeholder="e.g. HAIL-2026-SUP-1"
                value={draft.reference}
                onChange={(e) => change("reference", e.target.value)}
              />
            </Field>
          </Box>
          <Field label="Recovery title">
            <Input
              aria-label="Recovery title"
              value={draft.title}
              onChange={(e) => change("title", e.target.value)}
            />
          </Field>
          <Field label="Existing evidence report">
            <Select
              aria-label="Evidence report"
              value={draft.supplement || draft.coc || 0}
              onChange={(_, id) => {
                const source = (
                  draft.kind === "supplement"
                    ? detail.sources.supplements
                    : detail.sources.cocs
                ).find((s) => s.id === id);
                setDraft({
                  ...draft,
                  supplement: draft.kind === "supplement" ? id || null : null,
                  coc: draft.kind === "depreciation" ? id || null : null,
                  requested: source?.amount || draft.requested,
                });
              }}
            >
              <Option value={0}>Manual review / no report linked</Option>
              {(draft.kind === "supplement"
                ? detail.sources.supplements
                : detail.sources.cocs
              ).map((s) => (
                <Option key={s.id} value={s.id}>
                  Report #{s.id} · {money(s.amount)} ·{" "}
                  {s.packet_ready ? "PDF ready" : "Generate PDF from job first"}
                </Option>
              ))}
            </Select>
          </Field>
        </>
      )}
      <Box sx={field}>
        <Field label="Total requested for this item">
          <Input
            aria-label="Total requested"
            type="number"
            value={draft.requested}
            onChange={(e) => change("requested", e.target.value)}
          />
        </Field>
        <Field label="Reviewed policy (optional)">
          <Select
            aria-label="Reviewed policy"
            value={draft.analysis || 0}
            onChange={(_, id) =>
              setDraft({
                ...draft,
                analysis: id || null,
                source_page: id ? 1 : null,
              })
            }
          >
            <Option value={0}>Manual source reference</Option>
            {detail.analyses.map((a) => (
              <Option key={a.id} value={a.id}>
                {a.file_name} · review {a.revision}
              </Option>
            ))}
          </Select>
        </Field>
      </Box>
      {draft.analysis && (
        <Field label="Policy source page">
          <Input
            aria-label="Policy source page"
            type="number"
            value={draft.source_page || 1}
            onChange={(e) => change("source_page", Number(e.target.value))}
          />
        </Field>
      )}
      <Field label="Source and review note">
        <Textarea
          aria-label="Recovery source note"
          minRows={3}
          placeholder="Where does this amount come from? Include document, page, scope and review notes."
          value={draft.source_note}
          onChange={(e) => change("source_note", e.target.value)}
        />
      </Field>
      <Box
        component="details"
        open={!!draft.carrier_scope.length || !!draft.contractor_scope.length}
      >
        <Box
          component="summary"
          sx={{ cursor: "pointer", fontWeight: 600, mb: 1 }}
        >
          Compare carrier and contractor scope (optional)
        </Box>
        <Stack spacing={3}>
          <ScopeEditor
            label="Carrier"
            rows={draft.carrier_scope}
            onChange={(rows) => change("carrier_scope", rows)}
            measurement={null}
          />
          <ScopeEditor
            label="Contractor"
            rows={draft.contractor_scope}
            onChange={(rows) => change("contractor_scope", rows)}
            measurement={detail.sources.measurement}
          />
        </Stack>
      </Box>
      {!!error && <Alert color="danger">{message(error)}</Alert>}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button loading={pending} onClick={() => onSave(draft)}>
          Save reviewed draft
        </Button>
        <Button variant="plain" onClick={onCancel}>
          Cancel draft
        </Button>
      </Box>
    </Stack>
  );
}

function ActionForm({
  action,
  round,
  item,
  receipt,
  onSave,
  pending,
  error,
}: {
  action: RecoveryAction;
  round?: RecoveryRound;
  item?: RecoveryItem;
  receipt?: RecoveryReceipt;
  onSave: (data: Record<string, unknown>) => void;
  pending: boolean;
  error: unknown;
}) {
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState(
    action === "decision" ? round?.requested || "" : item?.outstanding || "",
  );
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [reference, setReference] = useState("");
  const [outcome, setOutcome] = useState("approved");
  const [ack, setAck] = useState(false);
  const submit = () =>
    onSave(
      action === "decision"
        ? { round: round?.id, outcome, approved_total: amount, note }
        : action === "receipt"
          ? { round: round?.id, amount, date, reference, note }
          : action === "reverse"
            ? { receipt: receipt?.id, note }
            : { round: round?.id, packet_reviewed: ack, note },
    );
  return (
    <Stack spacing={2} data-testid="recovery-action-form">
      <Typography level="h3">{labels[action]}</Typography>
      {round && (
        <Typography>
          {item?.title} · round {round.number} · requested{" "}
          {money(round.requested)}
        </Typography>
      )}
      {action === "submit" && (
        <>
          <Alert color="warning">
            Record this after reviewing and submitting the packet to the
            carrier. This action records the submission; it does not send an
            email.
          </Alert>
          <Checkbox
            label="I reviewed the packet and submitted it to the carrier"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
        </>
      )}
      {action === "decision" && (
        <>
          <Field label="Carrier decision">
            <Select
              aria-label="Carrier decision"
              value={outcome}
              onChange={(_, value) => {
                setOutcome(value || "approved");
                setAmount(
                  value === "rejected"
                    ? item?.approved || "0"
                    : round?.requested || "",
                );
              }}
            >
              <Option value="approved">Approved in full</Option>
              <Option value="partial">Partially approved</Option>
              <Option value="rejected">Additional request rejected</Option>
            </Select>
          </Field>
          <Typography>
            Previously approved: {money(item?.approved || 0)}. Enter the new
            total approved for this item, including prior approvals.
          </Typography>
        </>
      )}
      {["decision", "receipt"].includes(action) && (
        <Field
          label={
            action === "decision" ? "New approved total" : "Amount received"
          }
        >
          <Input
            aria-label={
              action === "decision" ? "Approved total" : "Receipt amount"
            }
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
      )}
      {action === "receipt" && (
        <>
          <Alert>
            This is a claim recovery record. Record the corresponding customer
            payment in Billing to update invoice balances.
          </Alert>
          <Box sx={field}>
            <Field label="Received date">
              <Input
                aria-label="Receipt date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field label="Unique payment reference">
              <Input
                aria-label="Receipt reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Field>
          </Box>
        </>
      )}
      {action === "reverse" && (
        <Alert color="warning">
          Reverse {money(receipt?.amount || 0)} ({receipt?.reference}). The
          original and reversal remain in the audit trail.
        </Alert>
      )}
      <Textarea
        aria-label="Recovery action note"
        placeholder="Decision source, submission reference or reason"
        minRows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {!!error && <Alert color="danger">{message(error)}</Alert>}
      <Button
        loading={pending}
        disabled={action === "submit" && !ack}
        onClick={submit}
      >
        {labels[action]}
      </Button>
    </Stack>
  );
}

export default function RecoveryWorkspace() {
  const [params, setParams] = useSearchParams();
  const jobId = Number(params.get("job")) || null;
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [all, setAll] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<{
    item?: RecoveryItem;
    revision: number;
    key: string;
  } | null>(null);
  const [action, setAction] = useState<{
    kind: RecoveryAction;
    round?: RecoveryRound;
    item?: RecoveryItem;
    receipt?: RecoveryReceipt;
    revision: number;
    key: string;
  } | null>(null);
  const [followup, setFollowup] = useState<{
    owner: number | null;
    date: string;
    next: string;
    revision: number;
    key: string;
  } | null>(null);
  const [pdf, setPdf] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const cache = useQueryClient();
  const board = useQuery({
    queryKey: ["recovery-board", applied, all, overdue, page],
    queryFn: async () =>
      (
        await api.get<RecoveryBoard>(
          `/api/policy-copilot/recovery/?q=${encodeURIComponent(applied)}&include_all=${all ? 1 : 0}&attention=${overdue ? "overdue" : ""}&page=${page}`,
        )
      ).data,
  });
  const detail = useQuery({
    queryKey: ["recovery-detail", jobId],
    enabled: !!jobId,
    queryFn: async () =>
      (
        await api.get<RecoveryDetail>(
          `/api/policy-copilot/recovery/jobs/${jobId}/`,
        )
      ).data,
  });
  const data = detail.data;
  const mutate = useMutation({
    mutationFn: async (input: {
      action: RecoveryAction;
      data: unknown;
      revision: number;
      request_key: string;
    }) =>
      (
        await api.post<RecoveryDetail>(
          `/api/policy-copilot/recovery/jobs/${jobId}/actions/`,
          input,
        )
      ).data,
    onSuccess: (result, variables) => {
      cache.setQueryData(["recovery-detail", jobId], result);
      void cache.invalidateQueries({ queryKey: ["recovery-board"] });
      setAction(null);
      setDraft(null);
      setFollowup(null);
      setStatus(
        `${labels[variables.action]} saved in review ${result.revision}.`,
      );
    },
  });
  const openAction = (
    kind: RecoveryAction,
    round?: RecoveryRound,
    item?: RecoveryItem,
    receipt?: RecoveryReceipt,
  ) => {
    if (!data) return;
    mutate.reset();
    setStatus("");
    setAction({
      kind,
      round,
      item,
      receipt,
      revision: data.revision,
      key: crypto.randomUUID(),
    });
  };
  const openJob = (id: number) => {
    const next = new URLSearchParams(params);
    next.set("job", String(id));
    next.set("workspace", "recovery");
    setParams(next);
    setStatus("");
    mutate.reset();
  };
  return (
    <Stack spacing={2} data-testid="recovery-workspace">
      <Card>
        <Typography level="h2">Insurance recovery</Typography>
        <Typography>
          Know what was requested, what the carrier approved and what has been
          received. Keep the next action with its owner.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          <Input
            aria-label="Find claim or job"
            placeholder="Find a job, claim or job number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                setApplied(search);
              }
            }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <Button
            onClick={() => {
              setPage(1);
              setApplied(search);
            }}
          >
            Search claims
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Checkbox
            label="Include jobs without a claim yet"
            checked={all}
            onChange={(e) => {
              setAll(e.target.checked);
              setPage(1);
            }}
          />
          <Checkbox
            label="Overdue follow-ups"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(1);
            }}
          />
        </Box>
      </Card>
      {board.error && (
        <Alert color="danger">
          {message(board.error)}
          <Button onClick={() => void board.refetch()}>Retry claims</Button>
        </Alert>
      )}
      <Card>
        <ResponsiveTable
          rows={board.data?.rows || []}
          getRowKey={(row) => row.job}
          columns={[
            {
              key: "job",
              header: "Job / claim",
              mobileVariant: "header",
              render: (row) => (
                <Button variant="plain" onClick={() => openJob(row.job)}>
                  {row.name}
                  <br />
                  {row.claim || row.job_number}
                </Button>
              ),
            },
            {
              key: "approved",
              header: "Approved",
              render: (row) => money(row.totals.approved),
            },
            {
              key: "received",
              header: "Received",
              render: (row) => money(row.totals.received),
            },
            {
              key: "outstanding",
              header: "Approved unpaid",
              render: (row) => money(row.totals.outstanding),
            },
            {
              key: "next",
              header: "Next action / owner",
              mobileVariant: "body",
              render: (row) => (
                <>
                  <Typography level="body-sm">
                    {row.next_action || "No next action recorded"}
                  </Typography>
                  <Typography level="body-xs">
                    {row.owner || "Unassigned"} · {localDate(row.next_followup)}
                  </Typography>
                  {row.overdue && <Chip color="warning">Overdue</Chip>}
                </>
              ),
            },
          ]}
          emptyState={
            board.isLoading
              ? "Loading claims…"
              : "No matching jobs. Include jobs without a claim or open Insurance recovery from a job."
          }
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Typography>
            Page {page} · {board.data?.count || 0} jobs
          </Typography>
          <Button
            variant="outlined"
            disabled={page * 20 >= (board.data?.count || 0)}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Box>
      </Card>
      {detail.error && (
        <Alert color="danger">
          {message(detail.error)}
          <Button onClick={() => void detail.refetch()}>Reload job</Button>
        </Alert>
      )}
      {data && (
        <Stack spacing={2} data-testid="recovery-job">
          <Card>
            <Typography level="h3">{data.job.name}</Typography>
            <Typography>
              {data.job.insurance_company || "Carrier not recorded"} ·{" "}
              {data.job.claim_number || "Claim number not recorded"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                component={Link}
                to={`/operations/jobs/${data.job.id}`}
                variant="outlined"
              >
                Open job and reports
              </Button>
              <Button
                component={Link}
                to={`/policy-copilot?job=${data.job.id}`}
                variant="outlined"
              >
                Review policy sources
              </Button>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" },
                gap: 1,
                mt: 1,
              }}
            >
              {[
                ["Requested", data.totals.requested],
                ["Carrier approved", data.totals.approved],
                ["Received", data.totals.received],
                ["Approved unpaid", data.totals.outstanding],
              ].map(([label, value]) => (
                <Card key={label} variant="outlined">
                  <Typography level="body-sm">{label}</Typography>
                  <Typography level="h3" sx={{ fontSize: { xs: 20, sm: 25 } }}>
                    {money(value)}
                  </Typography>
                </Card>
              ))}
            </Box>
            <Typography level="body-sm">
              Draft requests: {money(data.totals.draft_requested)} ·
              Depreciation requested but not received:{" "}
              {money(data.totals.depreciation_outstanding)} (includes amounts
              awaiting approval). Figures track this recovery ledger; invoice
              payments remain in Billing.
            </Typography>
            {data.sources.measurement ? (
              <Typography level="body-sm">
                ApexOS measurement:{" "}
                {Number(data.sources.measurement.squares).toFixed(2)} squares ·
                captured{" "}
                {new Date(
                  data.sources.measurement.updated_at,
                ).toLocaleDateString()}
              </Typography>
            ) : (
              <Typography level="body-sm">
                No completed ApexOS measurement selected on this job.
              </Typography>
            )}
            <Typography level="title-md">
              {data.next_action || "Set a next action"} ·{" "}
              {data.owners.find((o) => o.id === data.owner)?.name ||
                "Unassigned"}{" "}
              · {localDate(data.next_followup)}
            </Typography>
            {data.can_edit && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  onClick={() => {
                    mutate.reset();
                    setDraft({
                      revision: data.revision,
                      key: crypto.randomUUID(),
                    });
                  }}
                >
                  Add recovery item
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    mutate.reset();
                    setFollowup({
                      owner: data.owner,
                      date: data.next_followup || "",
                      next: data.next_action,
                      revision: data.revision,
                      key: crypto.randomUUID(),
                    });
                  }}
                >
                  Assign next action
                </Button>
              </Box>
            )}
          </Card>
          {status && <Alert color="success">{status}</Alert>}
          {mutate.error && !action && !draft && !followup && (
            <Alert color="danger">{message(mutate.error)}</Alert>
          )}
          {data.items.map((item) => (
            <Card key={item.id} data-testid={`recovery-item-${item.id}`}>
              <Typography level="h4">{item.title}</Typography>
              <Typography level="body-sm">
                {item.kind} · {item.reference} · approved {money(item.approved)}{" "}
                · received {money(item.received)} · unpaid{" "}
                {money(item.outstanding)}
              </Typography>
              {item.rounds.map((round) => (
                <Box
                  key={round.id}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 1.5,
                    mt: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    <Typography level="title-lg">
                      Round {round.number} · {money(round.requested)}
                    </Typography>
                    <Chip
                      color={
                        ["approved", "partial"].includes(round.status)
                          ? "success"
                          : round.status === "rejected"
                            ? "danger"
                            : "neutral"
                      }
                    >
                      {round.status}
                    </Chip>
                  </Stack>
                  <Typography>{round.source_note}</Typography>
                  <Typography level="body-xs">
                    Reviewed by {round.scope_snapshot.reviewer} ·{" "}
                    {new Date(
                      round.scope_snapshot.reviewed_at,
                    ).toLocaleString()}
                  </Typography>
                  {!!round.comparison.length && (
                    <Box component="details" sx={{ my: 1 }}>
                      <Box component="summary" sx={{ cursor: "pointer" }}>
                        Scope comparison ({round.comparison.length}{" "}
                        {round.comparison.length === 1 ? "row" : "rows"})
                      </Box>
                      <ResponsiveTable
                        rows={round.comparison}
                        getRowKey={(r) => r.key}
                        columns={[
                          {
                            key: "work",
                            header: "Work",
                            mobileVariant: "header",
                            render: (r) => r.description,
                          },
                          {
                            key: "carrier",
                            header: "Carrier",
                            render: (r) =>
                              r.carrier
                                ? `${r.carrier.quantity} ${r.carrier.unit} · ${money(r.carrier_total)}`
                                : "Not listed",
                          },
                          {
                            key: "contractor",
                            header: "Contractor",
                            render: (r) =>
                              r.contractor
                                ? `${r.contractor.quantity} ${r.contractor.unit} · ${money(r.contractor_total)}`
                                : "Not listed",
                          },
                          {
                            key: "delta",
                            header: "Difference",
                            render: (r) =>
                              r.amount_delta === null
                                ? "Units/categories differ"
                                : `${r.quantity_delta} quantity · ${money(r.amount_delta)}`,
                          },
                        ]}
                      />
                    </Box>
                  )}
                  {round.submitted_at && (
                    <Typography level="body-sm">
                      Submitted{" "}
                      {new Date(round.submitted_at).toLocaleDateString()} ·{" "}
                      {Math.max(
                        0,
                        Math.floor(
                          (Date.now() -
                            new Date(round.submitted_at).getTime()) /
                            86400000,
                        ),
                      )}{" "}
                      days ago
                    </Typography>
                  )}
                  {round.decided_at && (
                    <Typography level="body-sm">
                      Carrier decision: {round.decision_note} · approved total{" "}
                      {money(round.approved_total || 0)} ·{" "}
                      {new Date(round.decided_at).toLocaleDateString()}
                    </Typography>
                  )}
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}
                  >
                    {round.packet_url && (
                      <Button
                        variant="outlined"
                        onClick={() => setPdf(round.packet_url!)}
                      >
                        Preview round {round.number} PDF
                      </Button>
                    )}
                    {data.can_edit && round.status === "draft" && (
                      <>
                        {!round.packet_url && (
                          <Button
                            variant="outlined"
                            loading={mutate.isPending}
                            onClick={() =>
                              mutate.mutate({
                                action: "packet",
                                revision: data.revision,
                                request_key: crypto.randomUUID(),
                                data: { round: round.id },
                              })
                            }
                          >
                            Assemble round {round.number} PDF
                          </Button>
                        )}
                        <Button
                          disabled={!round.packet_url}
                          onClick={() => openAction("submit", round, item)}
                        >
                          Record round {round.number} submission
                        </Button>
                        <Button
                          variant="plain"
                          color="danger"
                          onClick={() => openAction("discard", round, item)}
                        >
                          Discard draft {round.number}
                        </Button>
                      </>
                    )}
                    {data.can_edit && round.status === "submitted" && (
                      <Button
                        onClick={() => openAction("decision", round, item)}
                      >
                        Record round {round.number} decision
                      </Button>
                    )}
                    {data.can_edit &&
                      ["approved", "partial", "rejected"].includes(
                        round.status,
                      ) &&
                      Number(item.outstanding) > 0 && (
                        <Button
                          onClick={() => openAction("receipt", round, item)}
                        >
                          Record round {round.number} receipt
                        </Button>
                      )}
                  </Box>
                  {!!round.receipts.length && (
                    <ResponsiveTable
                      rows={round.receipts}
                      getRowKey={(r) => r.id}
                      columns={[
                        {
                          key: "ref",
                          header: "Receipt reference",
                          mobileVariant: "header",
                          render: (r) => r.reference,
                        },
                        {
                          key: "amount",
                          header: "Received / reversed",
                          render: (r) => money(r.amount),
                        },
                        {
                          key: "date",
                          header: "Date",
                          render: (r) => localDate(r.date),
                        },
                        {
                          key: "note",
                          header: "Note",
                          mobileVariant: "body",
                          render: (r) => r.note,
                        },
                      ]}
                      rowActions={(r) =>
                        data.can_edit &&
                        Number(r.amount) > 0 &&
                        !item.rounds.some((other) =>
                          other.receipts.some(
                            (reversal) => reversal.reverses === r.id,
                          ),
                        ) ? (
                          <Button
                            variant="plain"
                            color="danger"
                            onClick={() =>
                              openAction("reverse", round, item, r)
                            }
                          >
                            Reverse receipt
                          </Button>
                        ) : null
                      }
                    />
                  )}
                </Box>
              ))}
              {data.can_edit &&
                !item.rounds.some((r) =>
                  ["draft", "submitted"].includes(r.status),
                ) && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      mutate.reset();
                      setDraft({
                        item,
                        revision: data.revision,
                        key: crypto.randomUUID(),
                      });
                    }}
                  >
                    Add another round for {item.title}
                  </Button>
                )}
            </Card>
          ))}
          {!!data.history.length && (
            <Card>
              <Box component="details">
                <Box
                  component="summary"
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                >
                  Review history ({data.history.length})
                </Box>
                {data.history.map((event) => (
                  <Box
                    key={event.revision}
                    sx={{
                      py: 1,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography level="title-sm">
                      Review {event.revision} ·{" "}
                      {labels[event.action as RecoveryAction] || event.action} ·{" "}
                      {event.actor}
                    </Typography>
                    <Typography level="body-sm">
                      {new Date(event.at).toLocaleString()} ·{" "}
                      {String(
                        event.data.note ||
                          event.data.source_note ||
                          event.data.next_action ||
                          "",
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}
        </Stack>
      )}
      <Modal open={!!draft} onClose={() => !mutate.isPending && setDraft(null)}>
        <ModalDialog
          sx={{
            width: "min(850px, calc(100vw - 24px))",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose aria-label="Close dialog" />
          {draft && data && (
            <DraftForm
              detail={data}
              item={draft.item}
              pending={mutate.isPending}
              error={mutate.error}
              onCancel={() => setDraft(null)}
              onSave={(values) =>
                mutate.mutate({
                  action: "round",
                  data: values,
                  revision: draft.revision,
                  request_key: draft.key,
                })
              }
            />
          )}
        </ModalDialog>
      </Modal>
      <Modal
        open={!!action}
        onClose={() => !mutate.isPending && setAction(null)}
      >
        <ModalDialog
          sx={{
            width: "min(600px, calc(100vw - 24px))",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose aria-label="Close dialog" />
          {action && (
            <ActionForm
              action={action.kind}
              round={action.round}
              item={action.item}
              receipt={action.receipt}
              pending={mutate.isPending}
              error={mutate.error}
              onSave={(values) =>
                mutate.mutate({
                  action: action.kind,
                  data: values,
                  revision: action.revision,
                  request_key: action.key,
                })
              }
            />
          )}
        </ModalDialog>
      </Modal>
      <Modal
        open={!!followup}
        onClose={() => !mutate.isPending && setFollowup(null)}
      >
        <ModalDialog
          sx={{
            width: "min(600px, calc(100vw - 24px))",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose aria-label="Close dialog" />
          {followup && data && (
            <Stack spacing={2}>
              <Typography level="h3">Assign next action</Typography>
              <Select
                aria-label="Recovery owner"
                value={followup.owner || 0}
                onChange={(_, id) =>
                  setFollowup({ ...followup, owner: id || null })
                }
              >
                <Option value={0}>Unassigned</Option>
                {data.owners.map((owner) => (
                  <Option key={owner.id} value={owner.id}>
                    {owner.name}
                  </Option>
                ))}
              </Select>
              <Input
                aria-label="Next follow-up date"
                type="date"
                value={followup.date}
                onChange={(e) =>
                  setFollowup({ ...followup, date: e.target.value })
                }
              />
              <Textarea
                aria-label="Next recovery action"
                value={followup.next}
                onChange={(e) =>
                  setFollowup({ ...followup, next: e.target.value })
                }
              />
              {mutate.error && (
                <Alert color="danger">{message(mutate.error)}</Alert>
              )}
              <Button
                loading={mutate.isPending}
                onClick={() =>
                  mutate.mutate({
                    action: "followup",
                    revision: followup.revision,
                    request_key: followup.key,
                    data: {
                      owner: followup.owner,
                      next_followup: followup.date || null,
                      next_action: followup.next,
                    },
                  })
                }
              >
                Save next action
              </Button>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
      {pdf && (
        <PdfViewerModal
          open
          onClose={() => setPdf(null)}
          pdfUrl={pdf}
          title="Recovery packet preview"
        />
      )}
    </Stack>
  );
}
